import {SEGMENT} from "@reva/shared/reva-spec";

import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {ArchiveDocumentType, FinalizeValuationResponse} from "../payload-types/persistence-finalize";
import type {ValueIndicationRequest, ValueIndicationResponse} from "../payload-types/value-indication";
import {ValidationErrorI18n} from "../validation";

export class FinalizeApiInterface extends SimpleApiInterface<FinalizeValuationResponse> {
    private readonly revaId: string;
    private readonly segment: string | null;
    private readonly finalParametersIn: object;
    private readonly valuationParamsOut: ValueIndicationResponse | null;
    private readonly valuationParamsIn: ValueIndicationRequest | null;
    private readonly documentTypes: ArchiveDocumentType[];

    constructor(
        revaId: string,
        segment: string | null,
        finalParametersIn: object,
        valuationParamsOut: ValueIndicationResponse | null,
        valuationParamsIn: ValueIndicationRequest | null,
        documentTypes: ArchiveDocumentType[],
    ) {
        super();
        this.revaId = revaId;
        this.segment = segment;
        this.finalParametersIn = finalParametersIn;
        this.valuationParamsOut = valuationParamsOut;
        this.valuationParamsIn = valuationParamsIn;
        this.documentTypes = documentTypes;
    }

    protected validate(): void {
        if (!this.valuationParamsOut) {
            throw new ValidationErrorI18n("valuationParamsOut", "object.error.finalizationMissingValuation");
        }
        if (!this.valuationParamsIn) {
            throw new ValidationErrorI18n("valuationParamsIn", "object.error.finalizationMissingValuation");
        }
        // Für alle Segmente (außer GRST_K) werden die EnergyValue-Details (unter `energy_value_details`)
        // für die Finalisierung benötigt (und serverseitig in REVA-REST validiert).
        // Grundstücke haben kein Gebäude und damit regulär keine Energiekennwert-Indikation.
        if (this.segment === SEGMENT.GRST_K) {
            return;
        }
        if (!this.valuationParamsOut.energy_value_details) {
            throw new ValidationErrorI18n("energy_value_details", "object.error.finalizationMissingEnergy");
        }
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_VALUATIONS, this.revaId, RevaApi.PATH_VALUATION_FINALIZE]);
    }

    protected buildPayload(): object {
        return {
            finalParametersIn: this.finalParametersIn,
            finalValuationParamsOut: this.valuationParamsOut,
            finalValuationParamsIn: this.valuationParamsIn,
            documentTypes: this.documentTypes,
        };
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<FinalizeValuationResponse> {
        return executor.fetchPOST(url, credentials, this.buildPayload());
    }
}
